import { Box } from '@mantine/core';
import { CSSProperties, ReactElement, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useCalendarDay, useNow } from '@/hooks/useNow';
import { alert } from '@/services/alert';
import { getRateLimitAlert, isMedplumRateLimitError } from '@/utils/medplumRateLimit';
import { ALL_POST_CONSULTATION_CONSULTS_QUERY } from '../../graphql';
import { useMarketOptions } from '@/hooks/useMarketOptions';
import { useHubFilterValues } from '../../hooks/useHubFilterValues';
import { usePacQueueHydration } from '../../hooks/usePacQueueHydration';
import { usePatientListFilters } from '../../hooks/usePatientListFilters';
import { QUEUE_PAGE_ROWS, useQueuePage } from '../../hooks/useQueuePage';
import { ConsultAppointmentResult } from '../../services/listRowShared';
import { lastCommunicationFloor } from '../../services/pacQueueHydration';
import {
  buildQueueConsultFilter,
  isInQueueConsultScope,
  postConsultationSubscriptionParams,
} from '../../services/postConsultationFilters';
import { buildPostConsultationRows, PostConsultationLeadRow } from '../../services/postConsultationRows';
import {
  isQueueFiltered,
  LEAD_STATUS_FILTER_OPTIONS,
  matchesPatientFilterTags,
  optionsFromRows,
  QUALIFICATION_FILTER_OPTIONS,
  QUEUE_FILTER_ANY,
  QUEUE_FILTERS_OFF,
  QueueFilterValues,
  TRACK_FILTER_OPTIONS,
  withPatientFilters,
} from '../../services/queueFilters';
import { ListRangeControl } from '@/components/Hub/ListRangeControl';
import { QueueFilterBar } from '@/components/Hub/QueueFilterBar';
import { QueueFooter } from '@/components/Hub/QueueFooter';
import { QueueTable } from '../../components/QueueTable';
import { lastContactColumn, patientColumn, paymentTrackColumn, pnColumn } from './hubColumns';
import { notesColumn } from './notesColumn';
import { consultColumn } from './postConsultColumns';
import { statusColumn } from './statusColumn';
import { EditedRow, useHubRowEdits } from './useHubRowEdits';

/**
 * The Intake hub's Post-consultation tab: the legacy `components/PostConsultationTab`
 * data, on the shared table, filter bar and footer. The legacy tab stays as it
 * was for the flag-off pill (BAR-2853 removes it).
 *
 * One row per patient, on their `latest-consult` (BAR-2865), so it pages 100 at
 * a time with a true total; the filters run on the server (BAR-2884). Status is
 * changeable from the row (BAR-2801).
 * Status defaults to Active, which is what the legacy tab's default "Inactive
 * hidden" chip did. The rows still carry the legacy tab's Quick note and Next
 * task; this tab doesn't show them.
 */

const DEFAULT_PRESET = 'all';

const CONSULT_PRESETS = [
  { id: 'all' as const, label: 'All time' },
  { id: 'last-30' as const, label: 'Last 30 days' },
  { id: 'last-60' as const, label: 'Last 60 days' },
];

const DEFAULTS: QueueFilterValues = { ...QUEUE_FILTERS_OFF, status: 'active' };

type HubPostConsultationRow = EditedRow<PostConsultationLeadRow>;

const HYDRATION_NEEDS = {
  procedure: true,
  consultNotes: true,
  callNotes: true,
  careTeam: true,
  eligibility: true,
  stickyNotes: true,
} as const;

const TAB_STYLE: CSSProperties = { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' };

export interface PostConsultationTabProps {
  /** Injectable for tests; defaults to a clock that keeps ticking. */
  now?: Date;
}

export function PostConsultationTab({ now }: PostConsultationTabProps): ReactElement {
  const navigate = useNavigate();
  const resolvedNow = useNow(now);
  const today = useCalendarDay(resolvedNow);

  const filters = usePatientListFilters<PostConsultationLeadRow>({
    resetKey: 'intake-post-consultation',
    chipDefinitions: [],
    defaultPreset: DEFAULT_PRESET,
    now: resolvedNow,
  });

  const { values, setValues, setFilter, defaults, pnOptions } = useHubFilterValues(DEFAULTS);
  const filter = useMemo(
    () => withPatientFilters(buildQueueConsultFilter(filters.range, today), values),
    [filters.range, today, values]
  );
  const subscriptionParams = useMemo(() => postConsultationSubscriptionParams(), []);

  const {
    rows: data,
    total,
    loading,
    error,
    page,
    setPage,
  } = useQueuePage(ALL_POST_CONSULTATION_CONSULTS_QUERY, 'Appointment', filter, subscriptionParams);

  useEffect(() => {
    if (error && isMedplumRateLimitError(error)) {
      alert.warning({
        ...getRateLimitAlert(
          `The system temporarily limited how fast we can load the post-consultation queue.
          Wait a few seconds before trying again.`
        ),
      });
    }
  }, [error]);

  const consults = useMemo(
    () =>
      error
        ? []
        : (data as ConsultAppointmentResult[]).filter((consult) => isInQueueConsultScope(consult, filters.range)),
    [data, error, filters.range]
  );

  const patientRefs = useMemo(
    () => [
      ...new Set(
        consults
          .flatMap((consult) => consult.participant ?? [])
          .map((participant) => participant.actor?.resource)
          .filter((resource) => resource?.resourceType === 'Patient')
          .map((resource) => `Patient/${(resource as { id?: string }).id}`)
      ),
    ],
    [consults]
  );

  const hydration = usePacQueueHydration(
    patientRefs,
    { ...HYDRATION_NEEDS, lastCommunicationSince: lastCommunicationFloor(today) },
    `intake-post-consultation:${filter}`
  );

  const builtRows = useMemo(
    // Missed consults belong to the Consultation no-show tab.
    () => buildPostConsultationRows(consults, hydration, resolvedNow, { includeNoShows: false }),
    [consults, hydration, resolvedNow]
  );
  const edits = useHubRowEdits(builtRows, values.status);
  const { rows } = edits;

  // Rows that arrive by subscription skip the query's filter, so PN, track and location
  // are checked on their tags too. Status reads the row, so a change made here applies at once.
  const visibleRows = useMemo(
    () =>
      rows.filter(
        (row) =>
          matchesPatientFilterTags(row.patient, { ...values, status: QUEUE_FILTER_ANY }) &&
          (values.status === QUEUE_FILTER_ANY || row.activeLead.status === values.status)
      ),
    [rows, values]
  );
  const { markets } = useMarketOptions();
  const locationOptions = useMemo(() => optionsFromRows(markets, 'All'), [markets]);

  const { onNoteSaved, changeStatus, undoStatus } = edits;
  const columns = useMemo(
    () => [
      patientColumn<HubPostConsultationRow>(),
      paymentTrackColumn<HubPostConsultationRow>(),
      pnColumn<HubPostConsultationRow>(),
      consultColumn<HubPostConsultationRow>(),
      lastContactColumn<HubPostConsultationRow>(),
      notesColumn<HubPostConsultationRow>({ onSaved: onNoteSaved }),
      statusColumn<HubPostConsultationRow>({ onChange: changeStatus, onUndo: undoStatus }),
    ],
    [onNoteSaved, changeStatus, undoStatus]
  );

  return (
    <Box style={TAB_STYLE}>
      <QueueFilterBar
        date={
          <ListRangeControl
            label="Consult"
            presets={CONSULT_PRESETS}
            presetId={filters.presetId}
            value={filters.range}
            onChange={filters.setRange}
          />
        }
        filters={[
          { label: 'PN', value: values.pn, options: pnOptions, onChange: setFilter('pn') },
          { label: 'Payment track', value: values.track, options: TRACK_FILTER_OPTIONS, onChange: setFilter('track') },
          { label: 'Location', value: values.location, options: locationOptions, onChange: setFilter('location') },
          {
            label: 'Qualified',
            value: values.qualification,
            options: QUALIFICATION_FILTER_OPTIONS,
            onChange: setFilter('qualification'),
          },
          { label: 'Status', value: values.status, options: LEAD_STATUS_FILTER_OPTIONS, onChange: setFilter('status') },
        ]}
        isFiltered={isQueueFiltered(values, defaults) || filters.presetId !== DEFAULT_PRESET}
        onReset={() => {
          setValues(defaults);
          filters.setRange(DEFAULT_PRESET);
        }}
      />
      <QueueTable
        columns={columns}
        data={visibleRows}
        loading={loading || hydration.loading}
        initialSorting={[{ id: 'consult', desc: true }]}
        onOpenPatient={(patientId) => navigate(`/Patient/${patientId}/timeline`)}
        getRowClassName={edits.rowClassName}
      />
      <QueueFooter
        shown={visibleRows.length}
        total={total}
        page={page}
        pageCount={Math.ceil((total ?? 0) / QUEUE_PAGE_ROWS)}
        onPageChange={setPage}
      />
    </Box>
  );
}
